/**
 * Wnioski pracowników — widok działu HR (PLAN §7 blok REQUESTS).
 *
 * Ekran nie ma makiety [D11]; układ powtarza wzorce listy pracowników: chipy
 * z licznikami, karty pozycji i decyzja w modalu. Liczniki liczy serwer
 * z realnych danych — pusta firma widzi stan pusty, nie przykładowe wnioski.
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Avatar,
  Button,
  Card,
  EmptyState,
  FormField,
  Inbox,
  InfoBanner,
  Modal,
  Spinner,
  StatusPill,
  cx,
  useToast,
} from '@k1p4/ui'
import { formatDate } from '@k1p4/i18n'
import { LEAVE_REQUEST_ERROR_CODES } from '@k1p4/shared'
import { ApiError, api, type HrRequestItem, type RequestStatus } from '../../api/client'
import styles from './Requests.module.css'

const FILTERS = ['pending', 'approved', 'rejected', 'all'] as const
type Filter = (typeof FILTERS)[number]

const STATUS_TONE: Record<RequestStatus, 'warning' | 'success' | 'danger'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
}

export function Requests() {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const commentId = useId()

  const [filter, setFilter] = useState<Filter>('pending')
  const [decision, setDecision] = useState<{ item: HrRequestItem; approve: boolean } | null>(null)
  const [comment, setComment] = useState('')
  const [error, setError] = useState<string | null>(null)
  /* Dni zwolnienia z wpisami pracy — kadry mogą zatwierdzić świadomie (fala 7b, grupa 133). */
  const [workDays, setWorkDays] = useState<readonly string[] | null>(null)

  const requests = useQuery({
    queryKey: ['requests', filter],
    queryFn: () => api.hrRequests(filter),
  })

  const decide = useMutation({
    mutationFn: (acknowledgeWorkEntries: boolean) =>
      api.decideRequest(
        decision!.item.id,
        decision!.approve ? 'approve' : 'reject',
        comment.trim() || undefined,
        acknowledgeWorkEntries,
      ),
    onSuccess: () => {
      toast.show(
        t(decision!.approve ? 'requestsView.approvedToast' : 'requestsView.rejectedToast'),
        { tone: decision!.approve ? 'success' : 'info' },
      )
      setDecision(null)
      setComment('')
      setWorkDays(null)
      void queryClient.invalidateQueries({ queryKey: ['requests'] })
    },
    onError: (caught) => {
      if (caught instanceof ApiError && caught.code === 'WORK_ENTRIES_IN_RANGE') {
        const days = caught.details.days
        setWorkDays(Array.isArray(days) ? days.map(String) : [])
        setError(null)
        return
      }
      /*
       * Stary wniosek urlopowy z kafla zatwierdza się przez urlopy — z pulą
       * i kontrolą terminu; odmowa ma nazwaną przyczynę (fala 8, grupa 40).
       */
      if (caught instanceof ApiError && caught.code === 'LEAVE_REQUEST_REQUIRED') {
        setError('requestsView.errorLeaveRequired')
        return
      }
      if (
        caught instanceof ApiError &&
        (LEAVE_REQUEST_ERROR_CODES as readonly string[]).includes(caught.code)
      ) {
        setError(`requestsView.leaveErrors.${caught.code}`)
        return
      }
      setError(
        /* Wniosek zostaje w kolejce — do odrzucenia z uzasadnieniem (grupa 166). */
        caught instanceof ApiError && caught.code === 'NO_HEALTH_INSURANCE_TITLE'
          ? 'requestsView.errorNoInsuranceTitle'
          : caught instanceof ApiError && caught.code === 'FAMILY_MEMBER_NOT_FOUND'
            ? 'requestsView.errorFamilyMemberMissing'
            : caught instanceof ApiError && caught.code === 'ENTITLEMENT_BEFORE_START'
              ? 'requestsView.errorEntitlementBeforeStart'
              : 'requestsView.errorGeneric',
      )
    },
  })

  if (requests.isPending) return <Spinner label={t('common.loading')} />
  if (requests.isError) {
    return <EmptyState title={t('common.error')} description={t('common.retry')} />
  }

  const { items, counts } = requests.data

  return (
    <>
      {error !== null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error)}
        </InfoBanner>
      ) : null}

      <div className={styles.chips}>
        {FILTERS.map((key) => (
          <button
            key={key}
            type="button"
            className={cx(styles.chip, filter === key && styles.chipActive)}
            aria-pressed={filter === key}
            onClick={() => setFilter(key)}
          >
            {t(`requestsView.filters.${key}`)}
            <span className={styles.chipCount}>{counts[key]}</span>
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <EmptyState
          icon={<Inbox size={28} />}
          title={t('requestsView.empty')}
          description={t('requestsView.emptyBody')}
        />
      ) : (
        <div className={styles.list}>
          {items.map((item) => (
            <Card key={item.id} pad="md" className={styles.card}>
              <Avatar name={`${item.firstName} ${item.lastName}`} size={40} />
              <div className={styles.body}>
                <p className={styles.type}>{t(`requestTypes.${item.type}`)}</p>
                <p className={styles.person}>
                  {item.firstName} {item.lastName}
                  {item.position ? ` · ${item.position}` : ''}
                </p>
                <p className={styles.meta}>
                  {t('requestsView.submittedAt', {
                    date: formatDate(item.createdAt, 'dot'),
                  })}
                  {item.dateFrom && item.dateTo
                    ? ` · ${t('requestsView.range', {
                        from: formatDate(item.dateFrom, 'dot'),
                        to: formatDate(item.dateTo, 'dot'),
                      })}`
                    : ''}
                  {item.decidedAt
                    ? ` · ${t('requestsView.decidedAt', {
                        date: formatDate(item.decidedAt, 'dot'),
                      })}`
                    : ''}
                </p>
                {/*
                  Wniosek o członka rodziny (Etap 37): kadry muszą widzieć, kogo
                  i od kiedy zgłosić — numer PESEL wyłącznie jako maska, bo to
                  dane osoby trzeciej.
                */}
                {item.family ? (
                  <p className={styles.note}>
                    {t(`requestsView.family.${item.family.action}`)}
                    {item.family.memberLabel ? ` · ${item.family.memberLabel}` : ''}
                    {item.family.kinshipCode
                      ? ` · ${t(`family.kinship.${item.family.kinshipCode}`, { defaultValue: item.family.kinshipName ?? item.family.kinshipCode })}`
                      : ''}
                    {item.family.pesel ? ` · ${item.family.pesel}` : ''}
                    {item.family.entitlementFrom
                      ? ` · ${t('requestsView.family.from', {
                          date: formatDate(item.family.entitlementFrom, 'dot'),
                        })}`
                      : ''}
                    {item.family.entitlementTo
                      ? ` · ${t('requestsView.family.to', {
                          date: formatDate(item.family.entitlementTo, 'dot'),
                        })}`
                      : ''}
                  </p>
                ) : null}
                {item.note ? <p className={styles.note}>{item.note}</p> : null}
                {item.decisionComment ? (
                  <p className={styles.note}>
                    {t('changeRequests.decisionComment', { text: item.decisionComment })}
                  </p>
                ) : null}
              </div>

              <div className={styles.side}>
                <StatusPill tone={STATUS_TONE[item.status]}>
                  {t(`portalRequestStatus.${item.status}`)}
                </StatusPill>
                {item.status === 'pending' ? (
                  <div className={styles.actions}>
                    <Button size="sm" onClick={() => { setError(null); setWorkDays(null); setDecision({ item, approve: true }) }}>
                      {t('requestsView.approve')}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => { setError(null); setWorkDays(null); setDecision({ item, approve: false }) }}
                    >
                      {t('requestsView.reject')}
                    </Button>
                  </div>
                ) : null}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={decision !== null}
        onClose={() => setDecision(null)}
        title={t('requestsView.decisionTitle')}
        footer={
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setDecision(null)}>
              {t('requestsView.cancel')}
            </Button>
            <Button disabled={decide.isPending} onClick={() => decide.mutate(workDays !== null)}>
              {t(
                workDays !== null
                  ? 'requestsView.confirmApproveWorkEntries'
                  : decision?.approve
                    ? 'requestsView.confirmApprove'
                    : 'requestsView.confirmReject',
              )}
            </Button>
          </div>
        }
      >
        {/* Overlay modala zakrywa baner strony — błąd musi być widoczny TUTAJ. */}
        {error !== null ? (
          <InfoBanner tone="danger" className={styles.banner}>
            {t(error)}
          </InfoBanner>
        ) : null}
        {workDays !== null ? (
          <InfoBanner tone="warning" className={styles.banner}>
            {t('requestsView.workEntriesWarning', {
              days: workDays.map((day) => formatDate(day, 'dot')).join(', '),
            })}
          </InfoBanner>
        ) : null}
        {decision ? (
          <p className={styles.modalSummary}>
            {t(`requestTypes.${decision.item.type}`)} · {decision.item.firstName}{' '}
            {decision.item.lastName}
          </p>
        ) : null}
        <FormField label={t('requestsView.decisionLabel')} htmlFor={commentId}>
          <textarea
            id={commentId}
            className={styles.textarea}
            rows={3}
            placeholder={t('requestsView.decisionPlaceholder')}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
        </FormField>
      </Modal>
    </>
  )
}
